<template>
    <div v-if="slicedItems.length > 0">
        <div v-if="showTitle" class="mb-3 flex items-center justify-between">
            <p class="text-sm font-semibold" v-text="$t('components.related_products.title')" />
            <button
                class="cursor-pointer text-xs font-semibold text-black underline"
                v-text="$t('components.related_products.show_all')"
                @click="scrollToRelated"
            />
        </div>

        <ProductPageSlider :items="slicedItems" :show-arrows="showArrows" :arrow-size="arrowSize" :track-class="showPrice ? 'gap-3' : 'gap-2'">
            <template v-if="currentImage" #before>
                <div
                    :class="showPrice || showName ? 'flex shrink-0 flex-col gap-1.5 sm:w-36' : 'block w-[calc(100%/4.5)] shrink-0 sm:w-20'"
                    :style="showPrice || showName ? { width: mobileItemWidth } : undefined"
                >
                    <div class="aspect-square w-full overflow-hidden rounded-md border-3 border-primary">
                        <img
                            :src="resolvedImageUrl(currentImage, useFullImage ? ImageTypeEnum.Mobile : ImageTypeEnum.Thumb)"
                            :alt="currentImageAlt"
                            class="h-full w-full object-cover"
                            loading="lazy"
                        />
                    </div>
                </div>
            </template>

            <template #default="{ item }">
                <Link
                    :href="index({ path: item.url_key })"
                    :class="showPrice || showName ? 'flex shrink-0 flex-col gap-1.5 sm:w-36' : 'block w-[calc(100%/4.5)] shrink-0 sm:w-20'"
                    :style="showPrice || showName ? { width: mobileItemWidth } : undefined"
                >
                    <div
                        class="aspect-square w-full overflow-hidden rounded-md border-3 border-transparent transition-colors duration-200 hover:border-primary"
                    >
                        <img
                            v-if="item.firstImage"
                            :src="resolvedImageUrl(item.firstImage, useFullImage ? ImageTypeEnum.Mobile : ImageTypeEnum.Thumb)"
                            :alt="item.name"
                            class="h-full w-full object-cover"
                            loading="lazy"
                        />
                    </div>

                    <template v-if="showPrice || showName">
                        <div class="flex flex-col gap-0.5">
                            <p class="line-clamp-2 text-xs leading-tight font-medium" v-text="item.name" />
                            <template v-if="showPrice">
                                <div class="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                                    <span
                                        v-if="item.price"
                                        class="text-sm font-semibold"
                                        :class="{ 'text-red-600': item.discount }"
                                        v-text="formatPrice(item.price)"
                                    />
                                    <span
                                        v-if="item.price && (item.recommended_price ?? 0) > item.price"
                                        class="text-sm text-gray-400 line-through"
                                        v-text="formatPrice(item.recommended_price)"
                                    />
                                </div>
                            </template>
                        </div>
                    </template>
                </Link>
            </template>
        </ProductPageSlider>
    </div>
</template>

<script setup lang="ts">
import ProductPageSlider from '@/components/sliders/ProductPageSlider.vue';
import { useFormatPrice } from '@/composables/useFormatPrice';
import type { CollectionSliderItemObject, ConfigurableProductProductImageObject } from '@/types/generated';
import { index } from '@/types/wayfinder/routes';
import { ImageTypeEnum, resolvedImageUrl } from '@/utils/imageUrl';
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';

const props = withDefaults(
    defineProps<{
        items: CollectionSliderItemObject[];
        showTitle?: boolean;
        showPrice?: boolean;
        showName?: boolean;
        showArrows?: boolean;
        arrowSize?: 'small' | 'large';
        maxItems?: number;
        mobileItemWidth?: string;
        useFullImage?: boolean;
        currentImage?: ConfigurableProductProductImageObject | null;
        currentImageAlt?: string;
    }>(),
    {
        showTitle: true,
        showPrice: true,
        showName: false,
        showArrows: true,
        arrowSize: 'small',
        maxItems: undefined,
        mobileItemWidth: '7rem',
        useFullImage: false,
        currentImage: null,
        currentImageAlt: undefined,
    },
);

const slicedItems = computed(() => (props.maxItems !== undefined ? props.items.slice(0, props.maxItems) : props.items));

const formatPrice = useFormatPrice();

function scrollToRelated(): void {
    document.getElementById('related-products')?.scrollIntoView({ behavior: 'smooth' });
}
</script>
